import { createSelector } from "@reduxjs/toolkit";
import moment from "moment";
import { jsonParse } from "@/shared/utils/json-parse";
import type { IPage, IVersion } from "@/types";
import type { RootState } from "../store";

// ============= UI =============
export const isDialogOpen = (state: RootState) => state.shareDialog.dialogOpen;
export const getShareDialogViewId = (state: RootState) =>
  state.shareDialog.viewId;
export const getLayout = (state: RootState) => state.shareDialog.layout;
export const getOgUploadStatus = (state: RootState) =>
  state.shareDialog.ogUploadStatus;

export const getAutogeneratePreviewVersionId = (state: RootState) =>
  state.shareDialog.autogeneratePreviewVersionId;
// ============= UI =============

// ============= PAGE =============
export const getPage = (state: RootState): IPage | null =>
  state.shareDialog.page;
export const getPageId = (state: RootState) => state.shareDialog.pageId || "";
export const getAcceptToken = createSelector(getPage, (page) => {
  if (!page) return null;
  return page.settings.acceptToken;
});
export const getContentPointer = createSelector(getPage, (page) => {
  if (!page) return null;
  const { publicContentPointer, contentPointer } = page.settings;
  return publicContentPointer || contentPointer;
});
export const getSlides = (state: RootState) => state.shareDialog.slides;
export const getPageTitle = createSelector(getPage, (page) => {
  if (!page) return null;
  return page.settings.title;
});
export const getPageFolderId = createSelector(getPage, (page) => {
  if (!page) return null;
  return page.folderId;
});
// ============= PAGE =============

// ============= VERSIONS =============
export const getVersions = (state: RootState) => state.shareDialog.versions;
export const getVersionsLoading = (state: RootState) =>
  state.shareDialog.versionsLoading;
export const getVersionHandleType = (state: RootState) =>
  state.shareDialog.versionHandleType;

export const getPrimaryVersion = (state: RootState): IVersion | null =>
  state.shareDialog.primaryVersion;

export const getCustomVersion = (state: RootState): IVersion | null => {
  return state.shareDialog.selectedVersion;
};

export const getSelectedVersion = createSelector(
  getPrimaryVersion,
  getCustomVersion,
  (primaryVersion, customVersion) => {
    if (customVersion) return customVersion;
    return primaryVersion;
  },
);

export const isPrimaryVersion = (id: string) =>
  createSelector(getPrimaryVersion, (version) => version?._id === id);

// ============ MULTI SELECT ============
export const getTotalVersionsCount = (state: RootState) =>
  state.shareDialog.totalVersionsCount;

export const getMultiSelectedVersions = (state: RootState) =>
  state.shareDialog.multiSelectedVersions as Record<string, string>;

export const getSelectionLength = createSelector(
  getMultiSelectedVersions,
  (selected) => Object.keys(selected).length,
);

export const hasSelectedVersions = createSelector(
  getSelectionLength,
  (selectedLength) => selectedLength > 0,
);

export const isVersionSelected = (id: string) =>
  createSelector(getMultiSelectedVersions, (selected) => !!selected[id]);

export const getUnsealedLiveVersionsCount = createSelector(
  getVersions,
  (versions) => {
    if (!versions) return 0;
    return versions.filter((version) => {
      const url = (version as Record<string, any>).url;
      const seal = (version as Record<string, any>).seal;
      return url && !seal?.length;
    }).length;
  },
);

export const isAllLiveSelected = createSelector(
  getMultiSelectedVersions,
  (selected) => selected.live === "live",
);

export const doesAllVersionsSelected = createSelector(
  getUnsealedLiveVersionsCount,
  getMultiSelectedVersions,
  (versions, selected) => {
    return versions === Object.keys(selected).length;
  },
);

export const getDisplayedCount = createSelector(
  getSelectionLength,
  isAllLiveSelected,
  getTotalVersionsCount,
  (selected, isAllSelection, totalCount) => {
    if (isAllSelection) return totalCount - 1;
    return selected;
  },
);
// ============ MULTI SELECT ============

// ============= VERSIONS =============

// ============= VERSION DATA =============
export const getVersionTitle = createSelector(
  getSelectedVersion,
  getPage,
  (version, page) => {
    if (!version) return null;
    if (page?.primaryVersionId === version._id)
      return page.settings.og?.siteName;
    return version.data.title;
  },
);

export const getVersionUrl = createSelector(getSelectedVersion, (version) => {
  if (!version) return null;
  return version.url;
});

export const getVersionId = createSelector(getSelectedVersion, (version) => {
  if (!version) return null;
  return version._id;
});

export const getIsStaticMode = (state: RootState) =>
  state.shareDialog.staticMode;

export const getIsVersionUnsynced = (updatedAt: string) =>
  createSelector(getPage, (page) => {
    if (!page) return false;
    const pageUpdatedAt = moment(page.publishedAt).format(
      "YYYY-MM-DDTHH:mm:ss",
    );
    const isAfter = moment(pageUpdatedAt).isAfter(updatedAt);
    return isAfter;
  });
// ============= VERSION DATA =============

// ============= DATA =============
export const getBulkSendView = (state: RootState) =>
  state.shareDialog.bulkSendView;
export const getSearchOptions = (state: RootState) =>
  state.shareDialog.searchOptions;
export const getPdfExportOptions = (state: RootState) =>
  state.shareDialog.pdfExportOptions;

export const getPageFsVars = createSelector(getPage, (page) => {
  const settings = page?.settings;
  const fastVars = settings?.fs["fs-vars"];

  if (fastVars) {
    const fastVarsArr = jsonParse(fastVars);
    if (fastVarsArr) return fastVarsArr;
  } else {
    return [];
  }
});

export const getOverrideContentPointer = (state: RootState) =>
  state.shareDialog.overrideContentPointer;
// ============= DATA =============
